import 'video.js/dist/video-js.css'
import videojs from 'video.js'
import de from 'video.js/dist/lang/de.json'
import nl from 'video.js/dist/lang/nl.json'
import i18next from 'i18next'
import Status from '../controls/Status'
import Title from '../controls/Title'
import { Box, LinearProgress, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useEffect, useRef, useState, useCallback } from 'react'
import { isMobile, isMobileOnly, useMobileOrientation } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { checkFile, fetchFile, getManagerSearchParams, isMobileApp, saveFile, sendDataToIframe, sendDataToMobileApp } from '../Utilities'

// register video.js language packs once at module load
if (i18next.language !== 'en') {
    videojs.addLanguage('de', de)
    videojs.addLanguage('nl', nl)
}

/**
 * Per-camera timelapse content — wraps a video.js player in the same layout grid the rest of
 * My Project/Timelapse uses (desktop multi-camera tiling + mobile stacking).
 *
 * The video.js setup (source-validation, player lifecycle, quality-menu component, snapshot +
 * download toolbar buttons) is inlined directly here; each camera gets its own instance with
 * its own refs/state.
 */
export default function TimelapseContent(props) {

    const { t } = useTranslation()
    const { breakpoints } = useTheme()
    const { contentHeight, camera, isSideDrawerOpen } = props
    const { timelapse: source = "", name: cameraName = "", id: cameraId = 0 } = camera
    const { isLandscape } = useMobileOrientation()
    const { CAMERAS, LAYOUT: { TITLE, WRAP_LIMIT }, DEBUG } = window.conf
    
    const [isDownloading, setIsDownloading] = useState(false)
    const [hasError, setHasError] = useState(false)
    const [sourcesVerified, setSourcesVerified] = useState(false)
    
    const videoRef = useRef(null)
    const playerRef = useRef(null)
    const reducedAvailableRef = useRef(false)
    const reducedActiveRef = useRef(false)
    const downloadVideoRef = useRef(() => {})
    
    const camCount = CAMERAS.length
    const manyCams = camCount > WRAP_LIMIT
    const moreThanOneCam = camCount > 1
    const isXl = useMediaQuery(breakpoints.up('xl'))
    const isMd = useMediaQuery(breakpoints.down('md'))
    const cameraLabel = cameraName
        ? `${cameraName}`
        : moreThanOneCam
            ? `${t("events.camera")} ${cameraId + 1}`
            : ""
    const title = TITLE ? (cameraLabel ? `${TITLE}_` : TITLE) : ""
    const sourceLowQuality = source.replace('timelapsecomplete.mp4', 'timelapsecomplete_reduced.mp4')
    const debug = DEBUG
    const targetFilename = `${title}${cameraLabel}${t("actions.screenshot")}`

    const playerOptions = {
        autoplay: false,
        muted: true,
        language: i18next.language,
        controls: true,
        responsive: true,
        preload: "metadata",
        nativeControlsForTouch: false, // use custom controls on mobile devices
        sources: [{ src: source }],
        playbackRates: [0.5, 1, 1.5, 2],
        controlBar: { 'volumePanel': false },
        experimentalSvgIcons: true,
        playsinline: true, // prefer non-fullscreen playback (iOS Safari default would be fullscreen)
        inactivityTimeout: isMobile ? 0 : 2000 // keep the controlbar always visible on mobile
    }

    const takeSnapshot = (video) => {
        const { isMobileApp: isMobileAppViaManager } = getManagerSearchParams()

        const canvas = document.createElement('canvas')
        const filename = `${targetFilename}.jpg`

        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height)

        const dataUrl = canvas.toDataURL('image/jpeg', 1.0)
        const msg = {
            type: "download",
            baseUrl: window.location.origin,
            sourceUrl: dataUrl,
            targetFilename: filename
        }

        if (isMobileApp) {
            sendDataToMobileApp(msg)
        } else if (isMobileAppViaManager) {
            sendDataToIframe(msg)
        } else {
            saveFile(dataUrl, filename)
        }
    }

    const downloadVideo = useCallback(() => {
        const finalSource = reducedActiveRef.current ? sourceLowQuality : source
        fetchFile(finalSource, `${targetFilename}.mp4`, setIsDownloading)
    }, [source, sourceLowQuality, targetFilename])

    const setupQualitySwitching = useCallback(async (player, playerControlBar) => {

        if (!reducedAvailableRef.current) {
            console.warn("[TimelapseContent] reduced timelapse not available, quality cannot be changed")
            return
        }

        let lastSeekTime = 0

        const vjsMenuButtonComponent = videojs.getComponent('MenuButton')
        const vjsMenuItemComponent = videojs.getComponent('MenuItem')

        class QualityMenu extends vjsMenuButtonComponent {

            constructor(player, options = {}) {
                super(player, options)

                const textElement = videojs.dom.createEl('span', {
                    className: 'vjs-quality-menu-text',
                    innerHTML: "Auto"
                })

                this.el().appendChild(textElement)
                this.el().title = t("timelapse.quality")

                this.addClass('vjs-quality-menu vjs-playback-rate vjs-menu-button vjs-menu-button-popup vjs-button')
                this.isSwitchingQuality = false
            }

            createItems() {
                const qualities = [
                    { label: t("timelapse.original"), src: source },
                    { label: t("timelapse.reduced"), src: sourceLowQuality }
                ]

                return qualities.map(quality => {
                    const menuItem = new vjsMenuItemComponent(this.player_, {
                        label: quality.label,
                        src: quality.src,
                        selectable: true,
                        className: "vjs-quality-menu-item",
                        selected: quality.label === t("timelapse.original")
                    })

                    menuItem.on('click', () => {
                        this.updateMenuSelection(menuItem)
                        this.updateMenuText(menuItem)
                        this.switchQuality(menuItem)
                    })

                    return menuItem
                })
            }

            switchQuality(selectedItem) {
                this.isSwitchingQuality = true

                const player = this.player_
                const currentTime = player.currentTime()
                const isPlaying = !player.paused()
                const src = selectedItem.options_.src

                player.src(src)
                reducedActiveRef.current = src === sourceLowQuality

                player.one('loadeddata', () => {
                    player.currentTime(currentTime)
                    if (isPlaying) player.play()
                    this.isSwitchingQuality = false
                })
            }

            switchQualityToReduced() {
                const lowQualityItem = this.menu.children_.find(item => item.options_.label === t("timelapse.reduced"))
                if (lowQualityItem) {
                    this.updateMenuSelection(lowQualityItem)
                    this.updateMenuText(lowQualityItem)
                    this.switchQuality(lowQualityItem)
                }
            }

            updateMenuSelection(selectedItem) {
                this.menu.children_.forEach(item => item.selected(false))
                selectedItem.selected(true)
            }

            updateMenuText(selectedItem) {
                const textElement = this.el().querySelector('.vjs-quality-menu-text')
                if (textElement) {
                    textElement.innerHTML = selectedItem.options_.label
                }
            }
        }

        videojs.registerComponent('QualityMenu', QualityMenu)

        playerControlBar?.addChild('QualityMenu', {}, 15)

        const qualityMenu = player?.controlBar?.getChild('QualityMenu')

        if (isMobile && qualityMenu) {
            debug && console.log("[TimelapseContent] mobile device detected, switching to low quality")
            qualityMenu.switchQualityToReduced()
        }

        player?.on("seeking", () => {
            lastSeekTime = Date.now()
        })

        player?.on('waiting', () => {
            const now = Date.now()
            const isManualSeek = now - lastSeekTime < 500

            if (isManualSeek && debug) {
                debug && console.log("[TimelapseContent] manual seek detected, quality remains unchanged")
            }

            if (!isManualSeek && !qualityMenu?.isSwitchingQuality && player.currentSrc()?.includes(source)) {
                debug && console.log("[TimelapseContent] buffering detected, switching to low quality")
                qualityMenu.switchQualityToReduced()
            }
        })
    }, [source, sourceLowQuality, t, debug])

    useEffect(() => {
        let isMounted = true
        const verifySources = async () => {
            const [origResult, reducedResult] = await Promise.all([checkFile(source), checkFile(sourceLowQuality)])

            if (!isMounted) return

            const hasOriginalSource = origResult.exists && origResult.contentType === 'video'
            const hasReducedSource = reducedResult.exists && reducedResult.contentType === 'video'

            if (!hasOriginalSource) {
                console.error(`[TimelapseContent] original source not available or not a video: ${source}`)
                setHasError(true)
                return
            }

            debug && console.log(`[TimelapseContent] reduced source available: ${hasReducedSource}`)
            reducedAvailableRef.current = hasReducedSource
            setSourcesVerified(true)
        }
        verifySources()
        return () => {
            reducedAvailableRef.current = false
            isMounted = false
        }
    }, [source, sourceLowQuality]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!sourcesVerified) return

        let player = playerRef.current

        if (!player) {
            const videoElement = videoRef.current
            if (!videoElement) return

            player = playerRef.current = videojs(videoElement, playerOptions)

            player.on('error', () => {
                const error = player.error()
                if (error) {
                    console.error(`[TimelapseContent] player error: ${error.message} (code ${error.code})`)
                    setHasError(error.code)
                }
            })

            const playerControlBar = player.getChild('ControlBar')

            playerControlBar.addChild('button', {
                controlText: t("actions.screenshot"),
                className: 'vjs-snapshot-button',
                clickHandler: () => takeSnapshot(videoElement)
            }, 13)

            !isMobile && playerControlBar.addChild('button', {
                controlText: t("actions.download"),
                className: 'vjs-download-button',
                clickHandler: () => downloadVideoRef.current() // always points to current function via ref below
            }, 14)

            setupQualitySwitching(player, playerControlBar)

        } else {
            player.src(playerOptions.sources)
            setHasError(false)
        }
    }, [source, videoRef, setupQualitySwitching, sourcesVerified]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const player = playerRef.current
        return () => {
            if (player && !player.isDisposed()) {
                player.dispose()
                playerRef.current = null
            }
        }
    }, [playerRef])

    useEffect(() => {
        downloadVideoRef.current = downloadVideo // keep ref pointing at the latest callback across re-renders
    }, [downloadVideo])

    if (hasError) {
        if (hasError === 4) { // MEDIA_ERR_SRC_NOT_SUPPORTED
            return (
                <Status
                    code={4}
                    controls={[ "header", "link" ]}
                    href={reducedActiveRef.current ? sourceLowQuality : source}
                />
            )
        }
        return <Status code={500} controls={[ "header" ]} />
    }

    return (
        <Box sx={{
            display: "flex",
            alignContent: 'center',
            justifyContent: "center",
            position: "relative"
        }}>
            <Title cameraName={cameraName} />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height:
                        isMobileOnly
                            ? isLandscape
                                ? contentHeight
                                : camCount < 2
                                    ? contentHeight
                                    : camCount < 3
                                        ? contentHeight / 2
                                        : 'unset'
                            : manyCams
                                ? 'unset'
                                : isXl && camCount > 2
                                    ? contentHeight / Math.ceil(camCount / 3)
                                    : contentHeight / Math.ceil(camCount / 2),
                    width: "100%",
                    border: "none"
                }}
                className={
                    isMobileOnly
                        ? isLandscape && isSideDrawerOpen && moreThanOneCam
                            ? 'unsetHeight'
                            : ''
                        : camCount < 2
                            ? 'heightWithoutHeader'
                            : camCount === 2
                                ? 'halfHeightWithoutHeader'
                                : 'unsetHeight'
                }
            >
                <Box sx={{
                    width: "100%",
                    height: "100%",
                    display: 'flex',
                    alignItems: 'center'
                }}>
                    <video
                        ref={videoRef}
                        className="video-js vjs-big-play-centered vjs-full-height"
                        style={{ padding: moreThanOneCam && !isMobile && !isMd ? "0.1rem" : "0" }}
                    />

                    {isDownloading && (
                        <div style={{
                            position: 'absolute',
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            bottom: 35
                        }}>
                            <Typography variant="caption" fontSize={16} color="white">
                                {t("actions.saving")}...
                            </Typography>
                            <LinearProgress style={{ width: '99%' }} />
                        </div>
                    )}
                </Box>
            </Box>
        </Box>
    )
}
